import { useCallback, useLayoutEffect, useRef, useState, type CSSProperties, type MutableRefObject, type ReactNode, type RefObject } from 'react'
import type { Evidence, IndexDef, ObjectiveEvent } from '@/api/types/readiness'
import { allObjectives, domainColor } from '@/lib/indexes/registry'
import { assertScale, fmtScore } from '@/lib/readiness/scale'
import { compareFigures, fmtMonthYear, levelReachedAt, looksLikeDate, pickMatrixColumns, type CompareColumn } from '@/lib/readiness/compare'
import { LIVE } from '@/lib/readiness/comparePair'
import { Chip, Icon } from '@/components/readiness/primitives'
import { FilterSelect } from '@/components/readiness/FilterSelect'
import { InfoTooltip, TipBody } from '@/components/readiness/Term'
import { CoverageChip } from '@/components/readiness/ProgressParts'
import { ObjectiveName } from '@/components/readiness/ObjectiveName'
import { OriginLink } from '@/lib/readiness/nav'
import { formatDate } from '@/components/common/format'

// ── Compare assessments (Iteration 6 §2, §4) ────────────────────────────────
// One card fusing the compare strip and the Level by assessment matrix: a
// header with the From → To pickers, the figures band, the matrix and a
// footer. The compared pair drives the band, the matrix tints and tags, the
// rail tags and the chart rings together (§2.2); `print` renders the Progress
// Report form (§11.3): full dates, plain text instead of links.
//
// The card sets the height of the row it shares with the assessment rail
// (display:flex column, footer pinned with margin-top:auto). The matrix shows
// at most five assessment columns on a fixed table layout so it never scrolls
// horizontally at ≥1100px; with no saved assessment the card renders its
// empty state (From disabled, a note in place of the band, a Pending
// confirmation column in place of What changed).

export type PairSource = 'picker' | 'rail' | 'chart'

const UP = 'var(--rd-status-ok-text)'
// Fixed column widths (px): code, context, From, To, What changed, and the
// least the objective name may take before a context column is dropped.
const COL = { code: 64, context: 96, pair: 104, changed: 220, nameMin: 150 }
const warn: CSSProperties = { color: 'var(--rd-status-warn)' }
const nowrap: CSSProperties = { whiteSpace: 'nowrap' }

interface Props {
  index: IndexDef
  columns: CompareColumn[]
  fromKey: string
  toKey: string
  onSetPair?: (from: string, to: string, source: PairSource) => void
  events: ObjectiveEvent[]
  evidence: Evidence[]
  q: string
  print?: boolean
  theme: 'light' | 'dark'
  // Discovery sync date for the footer's second line (moved here from the rail).
  syncDate?: string | null
  orgName?: string
  // The Progress page focuses the card after `Set as From` from the View all modal.
  focusRef?: RefObject<HTMLElement>
}

export function CompareAssessments({ index, columns, fromKey, toKey, onSetPair, events, evidence, q, print = false, theme, syncDate = null, orgName, focusRef }: Props) {
  const scale = assertScale(index)
  const items = allObjectives(index)
  const empty = columns.length <= 1
  const liveCol = columns[columns.length - 1]
  const from = (empty ? liveCol : columns.find((c) => c.key === fromKey)) ?? columns[0]
  const to = (empty ? liveCol : columns.find((c) => c.key === toKey)) ?? liveCol
  const f = compareFigures(index, events, evidence, from, to)
  const fmt = (v: number) => (scale === 'levels' ? String(v) : v.toFixed(1))
  const valueOf = (c: CompareColumn, code: string) => (scale === 'levels' ? (c.levels[code] ?? 'L0') : fmtScore(c.scores[code] ?? null))
  // How many context columns fit beside the fixed ones without a horizontal
  // scroll: three on a 1440 viewport, fewer as the card narrows; print keeps three.
  const sectionRef = useRef<HTMLElement | null>(null)
  const [contextMax, setContextMax] = useState(3)
  const setRefs = useCallback((el: HTMLElement | null) => {
    sectionRef.current = el
    if (focusRef) (focusRef as MutableRefObject<HTMLElement | null>).current = el
  }, [focusRef])
  useLayoutEffect(() => {
    const el = sectionRef.current
    if (!el || print || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const inner = el.clientWidth - 40
      const spare = inner - COL.code - 2 * COL.pair - COL.changed - COL.nameMin
      setContextMax(Math.max(0, Math.min(3, Math.floor(spare / COL.context))))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [print])
  const shown = empty ? [liveCol] : pickMatrixColumns(columns, from.key, to.key, print ? 3 : contextMax)
  const pairLabel = `${from.label} → ${to.label}`
  const codeList = (codes: string[], extra?: (code: string) => ReactNode) => codes.map((c, i) => <span key={c}>{i > 0 ? ' · ' : ''}<span className="rd-mono rd-id">{c}</span>{extra?.(c)}</span>)
  const unconfirmedNote = print ? <span style={warn}>unconfirmed suggestion, not yet recorded</span> : null
  const assetSub = (a: number | null, b: number | null) => {
    if (a == null || b == null) return ''
    const d = b - a
    return d === 0 ? 'No change' : `${d > 0 ? '+' : '−'}${Math.abs(d).toLocaleString()}`
  }
  const coverageTag = (inline = false) => (print ? <span className="rd-chip" style={{ background: 'var(--rd-coverage-bg)', color: 'var(--rd-coverage)', fontWeight: 600 }}>coverage-driven</span> : <CoverageChip inline={inline} />)
  const headerLine2 = (c: CompareColumn) => (c.key === LIVE ? formatDate(c.date) : looksLikeDate(c.label) ? c.savedBy ?? formatDate(c.date) : formatDate(c.date))
  const allShown = shown.length === columns.length

  return (
    <section ref={setRefs} tabIndex={-1} className="rd-card rd-compare-card min-w-0 p-5" aria-labelledby="rd-compare" data-testid="compare-assessments">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 id="rd-compare" className="rd-section-title">Compare assessments</h2>
            <Chip tone="tile" className="rd-chip-record">RECORD</Chip>
          </div>
          <p className="mt-1 max-w-3xl text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="compare-subtitle">
            Levels as recorded at each saved assessment, and how the compared pair differs. {print ? <>Comparing <b>{pairLabel}</b>.</> : 'Pick a pair here, from the rail, or by clicking a dot on the chart.'}
          </p>
        </div>
        {!print && onSetPair && (
          <div className="flex items-center gap-2 text-[12.5px]" data-testid="compare-pickers">
            {empty ? (
              <button type="button" className="rd-trigger rd-trigger-compact" disabled aria-disabled="true" style={{ color: 'var(--rd-text-faint)', cursor: 'not-allowed' }} data-testid="compare-from" data-value="">
                <span className="rd-trigger-label">From:</span>
                <span className="min-w-0 truncate">No saved assessment</span>
                <Icon name="chevron" size={14} className="rd-trigger-chevron" />
              </button>
            ) : (
              <FilterSelect compact label="From" value={from.key} onChange={(v) => onSetPair(v, to.key, 'picker')} options={columns.map((c) => [c.key, c.label])} testId="compare-from" />
            )}
            <span aria-hidden="true" style={{ color: 'var(--rd-text-muted)' }}>→</span>
            <FilterSelect compact label="To" value={to.key} onChange={(v) => onSetPair(from.key, v, 'picker')} options={columns.map((c) => [c.key, c.label])} testId="compare-to" />
          </div>
        )}
      </div>

      {/* Figures band: six equal cells (3 × 2 under 1100px of container width). */}
      {empty ? (
        <div className="rd-figures-box mt-3" data-testid="compare-empty">
          <p className="text-[14px] font-bold">Nothing to compare yet</p>
          <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Save an assessment to create {orgName || 'the organisation'}’s first record. It becomes the baseline, and every later change is measured from it.</p>
        </div>
      ) : (
        <div className="rd-figures mt-3" data-testid="compare-strip">
          <Figure label="Score" value={<>{fmt(from.score)} → {fmt(to.score)}</>} sub={from.key === to.key ? 'pick two columns' : <span style={{ color: f.scoreDelta > 0 ? 'var(--rd-status-ok-text)' : f.scoreDelta < 0 ? 'var(--rd-status-warn)' : undefined }} data-testid="compare-score-delta">{f.scoreDelta > 0 ? `▲ +${f.scoreDelta}` : f.scoreDelta < 0 ? `▼ ${f.scoreDelta}` : '= No change'}</span>} />
          <Figure
            label="Moved up · down"
            value={<>{f.movedUp.length} · <span style={f.movedDown.length ? warn : undefined}>{f.movedDown.length}</span></>}
            sub={
              f.movedUp.length || f.movedDown.length || f.coverageDown.size ? (
                <>
                  {codeList(f.movedUp, (c) => (f.unconfirmedMoves.has(c) ? <span style={warn}> unconfirmed</span> : null))}
                  {f.movedDown.length ? <>{f.movedUp.length ? ' · ' : ''}<span style={warn}>{codeList(f.movedDown)} ▼</span></> : null}
                  {f.coverageDown.size ? <>{f.movedUp.length || f.movedDown.length ? ' · ' : ''}<span style={{ color: 'var(--rd-coverage)' }}>{codeList(Array.from(f.coverageDown))} ◆</span></> : null}
                </>
              ) : ''
            }
          />
          <Figure label="Evidence added" value={f.evidenceCount} sub="" />
          <Figure label={scale === 'levels' ? 'Still at L0' : 'Not yet scored'} value={f.stillL0.length} sub={f.stillL0.length ? codeList(f.stillL0) : ''} />
          <Figure label="Assets not migrated" value={<>{from.notMigrated != null ? from.notMigrated.toLocaleString() : '—'} → {to.notMigrated != null ? to.notMigrated.toLocaleString() : '—'}</>} sub={assetSub(from.notMigrated, to.notMigrated)} />
          <Figure label="Assets known" value={<>{from.known != null ? from.known.toLocaleString() : '—'} → {to.known != null ? to.known.toLocaleString() : '—'}</>} sub={assetSub(from.known, to.known)} />
        </div>
      )}

      {/* Matrix: fixed layout, at most five assessment columns, no horizontal scroll at ≥1100px. */}
      <div className="rd-matrix-wrap mt-3">
        <table className="rd-matrix w-full text-[13.5px]" data-testid="compare-matrix" data-columns={shown.length}>
          <colgroup>
            <col style={{ width: COL.code }} />
            <col />
            {shown.map((c) => <col key={c.key} style={{ width: c.key === from.key || c.key === to.key ? COL.pair : COL.context }} />)}
            <col style={{ width: empty ? 180 : COL.changed }} />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
              <th className="py-2 pl-1 pr-2 text-left align-bottom font-medium" style={{ color: 'var(--rd-text-muted)' }} colSpan={2}>{scale === 'levels' ? 'Objective' : 'Sub-index'}</th>
              {shown.map((c) => {
                const tag = empty ? (c.key === LIVE ? 'TO' : null) : c.key === from.key ? 'FROM' : c.key === to.key ? 'TO' : null
                return (
                  <th key={c.key} className={`px-1.5 py-2 text-left align-bottom font-medium ${tag ? 'rd-pair-col' : ''}`} style={{ color: tag ? 'var(--rd-text-primary)' : 'var(--rd-text-muted)' }} data-testid={`matrix-col-${c.key}`}>
                    {tag && <span className="rd-pair-tag mb-1" data-testid={`matrix-tag-${c.key}`}>{tag}</span>}
                    <span className="rd-clamp-2 block" title={c.label}>{c.label}</span>
                    <span className="rd-mono block truncate text-[11px] font-normal" style={{ color: 'var(--rd-text-faint)', ...nowrap }}>{headerLine2(c)}</span>
                  </th>
                )
              })}
              <th className="py-2 pl-2 text-left align-bottom font-medium" style={{ color: 'var(--rd-text-muted)' }}>
                {empty ? <span className="font-medium">Pending confirmation</span> : print ? <span className="font-medium">What changed</span> : <InfoTooltip width={360} testId="what-changed-tooltip" content={
                  <TipBody title="What changed">
                    <p>The difference between the two assessments being compared: <b>{pairLabel}</b>. To compare a different pair, change From and To in the header above.</p>
                    <p><b className="rd-mono">L0 → L2</b> · The level moved. Unconfirmed suggestions are included and marked <i>unconfirmed</i>.</p>
                    <p><b>Same since Jun 2026</b> · No move between the compared pair. The date is when the level was last reached.</p>
                    <p>Coverage-driven drops are labelled ◆.</p>
                  </TipBody>
                }><span className="font-medium">What changed</span></InfoTooltip>}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map(({ domain, objective }) => {
              const code = objective.code
              const d = empty ? 0 : f.delta(code)
              const changed = d !== 0
              const color = domainColor(domain, theme)
              const coverage = changed && d < 0 && f.coverageDown.has(code)
              const pending = liveCol.suggestedOnConfirmed[code]
              return (
                <tr key={code} style={{ borderBottom: '1px solid var(--rd-border-subtle)', background: changed ? `${color}12` : undefined, borderLeft: changed ? `3px solid ${color}` : '3px solid transparent' }} data-testid={`matrix-row-${code}`}>
                  <td className="py-2 pl-1 pr-2 align-middle"><span className="rd-mono rd-id" style={{ color: 'var(--rd-text-muted)', ...nowrap }}>{code}</span></td>
                  <td className="py-2 pr-2 align-middle">
                    {print ? <ObjectiveName name={objective.title} style={{ maxWidth: 'none' }} /> : (
                      <OriginLink to={`/readiness/${code}${q}`} className="block hover:underline" style={{ color: 'inherit' }}>
                        <ObjectiveName name={objective.title} style={{ maxWidth: 'none' }} />
                      </OriginLink>
                    )}
                  </td>
                  {shown.map((c) => {
                    const isTo = !empty && c.key === to.key
                    const inPair = empty ? c.key === LIVE : isTo || c.key === from.key
                    const value = valueOf(c, code)
                    const live = c.key === LIVE
                    const flagged = live ? c.suggestedOnConfirmed[code] : undefined
                    return (
                      <td key={c.key} className={`px-1.5 py-2 align-middle ${inPair ? 'rd-pair-col' : ''}`} data-testid={`matrix-cell-${c.key}-${code}`}>
                        {/* Parts stack; each part stays on one line and never exceeds the column. */}
                        <span className="rd-cell-stack">
                          {isTo && changed ? (
                            <Chip tone="tile" style={{ ...(d > 0 ? { background: `${color}22`, color } : { background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' }), ...nowrap }}>
                              <span className="rd-mono">{value}</span> {d > 0 ? '▲' : '▼'}
                            </Chip>
                          ) : (
                            <span className="rd-mono" style={{ color: 'var(--rd-text-muted)', ...nowrap }}>{value}</span>
                          )}
                          {isTo && coverage && coverageTag()}
                          {flagged && !empty && <span className="rd-chip rd-chip-sm" style={{ border: '1px dashed var(--rd-border-control)', color: 'var(--rd-text-muted)', background: 'transparent', ...nowrap }} data-testid={`matrix-suggested-${code}`}>{flagged} suggested</span>}
                          {live && c.unconfirmed.has(code) && <span className="text-[11px]" style={{ ...warn, ...nowrap }} data-testid={`matrix-unconfirmed-${code}`}>unconfirmed</span>}
                        </span>
                      </td>
                    )
                  })}
                  {empty ? (
                    <td className="py-2 pl-2 align-middle text-[12.5px]" data-testid={`pending-${code}`}>
                      {pending && (
                        <span className="rd-changed">
                          <span className="rd-chip rd-chip-sm" style={{ border: '1px dashed var(--rd-border-control)', color: 'var(--rd-text-muted)', background: 'transparent', ...nowrap }} data-testid={`matrix-suggested-${code}`}>{pending} suggested</span>
                          {print ? unconfirmedNote : <OriginLink to={`/readiness/${code}${q}`} className="rd-link" originKey={`review-${code}`} style={nowrap}>Review →</OriginLink>}
                        </span>
                      )}
                    </td>
                  ) : (
                    <td className="py-2 pl-2 align-middle text-[12.5px]" data-testid={`what-changed-${code}`}>
                      {changed ? (
                        <span className="rd-changed">
                          <span style={nowrap}><span className="rd-mono rd-id">{valueOf(from, code)} → {valueOf(to, code)}{coverage ? ' ◆' : ''}</span>{coverage ? null : <> <span style={{ color: d > 0 ? UP : 'var(--rd-status-warn)' }}>{d > 0 ? '▲' : '▼'}</span></>}</span>
                          {coverage && coverageTag(true)}
                          {f.unconfirmedMoves.has(code) && (print ? unconfirmedNote : <><span style={{ ...warn, ...nowrap }}>unconfirmed</span><OriginLink to={`/readiness/${code}${q}`} className="rd-link" originKey={`open-${code}`} style={nowrap}>Open →</OriginLink></>)}
                        </span>
                      ) : (
                        <span className="rd-changed" style={{ color: 'var(--rd-text-muted)' }}>
                          <span style={nowrap}>Same since {fmtMonthYear(levelReachedAt(events, code, to.date))}</span>
                          {to.key === LIVE && to.suggestedOnConfirmed[code] && (print ? unconfirmedNote : <OriginLink to={`/readiness/${code}${q}`} className="rd-link" originKey={`review-${code}`} style={nowrap}>Review →</OriginLink>)}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Footer: pinned to the card's bottom when the rail's minimum height stretches the card. */}
      <div className="rd-compare-foot mt-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-1 text-[12px]" style={{ color: 'var(--rd-text-faint)' }}>
        <div className="min-w-0">
          <p>Unconfirmed suggestions appear in Live but are recorded at their last confirmed {scale === 'levels' ? 'level' : 'score'} when saved.</p>
          {syncDate && <p data-testid="compare-sync-note">Asset figures from Discovery · sync {formatDate(syncDate)}.</p>}
        </div>
        {!allShown && (
          <p className="shrink-0" style={{ color: 'var(--rd-text-muted)' }} data-testid="matrix-showing">
            Showing {shown.length} of {columns.length}{print ? ' · every saved assessment is listed under Saved assessments' : ' · change From to see others'}
          </p>
        )}
      </div>
    </section>
  )
}

// One cell of the figures band: label, mono value and a one-line sub-line
// that always reserves its height so the six cells align.
function Figure({ label, value, sub }: { label: string; value: ReactNode; sub: ReactNode }) {
  return (
    <div className="rd-figure" data-testid={`figure-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <span className="rd-figure-label">{label}</span>
      <span className="rd-figure-value rd-mono">{value}</span>
      <span className="rd-figure-sub" title={typeof sub === 'string' ? sub : undefined}>{sub}</span>
    </div>
  )
}
